/**
 * Selleasy campaign products in the cart drawer's "Complete your order"
 * carousel (snippets/cart-drawer-upsell.liquid).
 *
 * The Selleasy app embed validates its in-cart upsell campaign (today
 * "EX Items") against the cart and injects
 *   <lb-upsell-widget widget="widget#icuc" offer="offer#xxxxxxxx"
 *                     lb-trigger-product-id="<product id of the first line>">
 * after #cart-drawer-form; cart-drawer-redesign.css hides that widget. This
 * module uses the offers Selleasy validated for the cart (window.lbValidOffers,
 * which keeps every validation and survives the widget being wiped by a
 * drawer re-render) and that widget as the "there is a valid offer for this
 * cart" signals, with window.lbSetup.offers (the campaign config Selleasy's
 * script loads) as the product list, then shows those products with the
 * theme's own product-card-v3 cards, fetched from
 * sections/cart-upsell-cards.liquid, in place of the recommendation cards,
 * priced with the campaign discount.
 *
 * Lines added from those cards carry the line property
 *   _lb-product = "<trigger product id>#<offer id>"   e.g. 8625088430329#offer#55278327
 * which is what Selleasy's discount function keys on, and the campaign's
 * discount code (CART15#4834 today) is put on the cart through the Ajax
 * API (POST cart/update.js { discount }) as soon as the cards show, the
 * way Selleasy's own widget applies it, so the campaign discount (25%
 * today) applies to those lines. The pills then show it as "25% off"
 * (snippets/cart-discount-pills.liquid).
 *
 * Anything missing or unexpected (app disabled, no offer for this cart,
 * setting off, fetch error, config shape changed) leaves the default flow
 * untouched: the recommendations load as before. Entry points:
 * cart-drawer.js#loadUpsell() and cart-items.js#updateRecommendations()
 * call claim() before init(); a MutationObserver on #cart-drawer reacts to
 * Selleasy (re)injecting or updating its widget after every cart change.
 */
(() => {
  if (window.cibSelleasyUpsell) return;

  const CARD_TTL = 10 * 60 * 1000;
  const CODE_TTL = 60 * 1000;
  const WIDGET_WAIT = 1500;
  const WIDGET_TYPE = 'widget#icuc';
  const WIDGET_SELECTOR = `lb-upsell-widget[widget="${WIDGET_TYPE}"][offer]`;
  const PROPERTY_NAME = 'properties[_lb-product]';
  const PROMO_CLASS = 'cart-drawer-upsell__promo';
  // Set by the Liquid on first paint; keeps the carousel invisible until the
  // campaign cards are in or the fallback is decided (no flash of the
  // collection picks). Lives on the element itself, so it survives init().
  const PENDING_CLASS = 'cart-drawer-upsell--pending';

  const state = {
    status: 'unknown', // 'unknown' | 'active' | 'inactive'
    cards: new Map(), // handle -> { html, time } ('' html = missing or sold out)
    gen: 0,
    syncTimer: 0,
    waitTimer: 0,
    inflight: null, // { el, key }
    observer: null,
    observedRoot: null,
    code: '', // the active campaign's discount code
    codeCheckedAt: 0
  };

  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const getDrawer = () => document.getElementById('cart-drawer');
  const getUpsell = () => document.getElementById('cart-recommendations');
  const isEnabledFor = (el) => !!el && el.dataset.upsellSelleasy === 'true';

  const numericId = (gid) => {
    const last = String(gid || '').split('/').pop();
    const n = Number(last);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  /* ---------------------------------------------------------------- state */

  function clearWait() {
    if (state.waitTimer) {
      clearTimeout(state.waitTimer);
      state.waitTimer = 0;
    }
  }

  function reveal(el) {
    if (el) el.classList.remove(PENDING_CLASS);
  }

  /**
   * Keeps the theme's own recommendations load from replacing the campaign
   * cards. <product-recommendations>.init() returns early without
   * data-product-id, and it isn't only called through claim(): the element's
   * lazy loader (initLazyScript in main.js, an IntersectionObserver bound at
   * construction) fires it when the drawer opens and the element comes into
   * view. The id is parked in data-upsell-held-product-id and put back by
   * releaseDefault() before a fallback load.
   */
  function holdDefault(el) {
    if (!el || !el.dataset.productId) return;
    el.dataset.upsellHeldProductId = el.dataset.productId;
    delete el.dataset.productId;
  }

  function releaseDefault(el) {
    if (!el) return;
    if (el.dataset.upsellHeldProductId) {
      el.dataset.productId = el.dataset.upsellHeldProductId;
      delete el.dataset.upsellHeldProductId;
    }
    if (!el.dataset.productId) {
      const root = getDrawer();
      const firstLine = root && root.querySelector('.cart-item[data-product-id]');
      if (firstLine && firstLine.dataset.productId) el.dataset.productId = firstLine.dataset.productId;
    }
  }

  /** Loads the default recommendations into the element (the theme's own flow). */
  function fallback(el) {
    const target = el || getUpsell();
    reveal(target);
    if (!target || typeof target.init !== 'function') return;
    releaseDefault(target);
    delete target.dataset.upsellSource;
    delete target.dataset.upsellOffer;
    delete target.dataset.upsellTrigger;
    target.classList.remove('is-loading');
    try {
      target.init();
    } catch (e) {
      // ignore: the first paint already shows the collection picks
    }
  }

  function markInactive(el) {
    state.status = 'inactive';
    state.inflight = null;
    clearWait();
    fallback(el);
  }

  /**
   * Gives Selleasy a moment to answer (its widget or a validated offer)
   * after a claim or a re-render; if it hasn't, the default recommendations
   * load. A render in flight means it did answer: leave it to finish.
   */
  function armWait() {
    if (state.waitTimer) return;
    state.waitTimer = setTimeout(() => {
      state.waitTimer = 0;
      if (state.inflight) return;
      const root = getDrawer();
      if (root && root.querySelector(WIDGET_SELECTOR)) return;
      if (validatedOffer()) return;
      markInactive(getUpsell());
    }, WIDGET_WAIT);
  }

  function scheduleSync() {
    if (state.syncTimer) return;
    state.syncTimer = setTimeout(() => {
      state.syncTimer = 0;
      try {
        sync();
      } catch (e) {
        markInactive(getUpsell());
      }
    }, 0);
  }

  function observe() {
    const root = getDrawer();
    if (!root || state.observedRoot === root) return;
    if (state.observer) state.observer.disconnect();
    state.observer = new MutationObserver(scheduleSync);
    state.observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['offer', 'lb-trigger-product-id']
    });
    state.observedRoot = root;
  }

  /* ------------------------------------------------------ Selleasy config */

  const shortIdOf = (offer) => (
    offer && typeof offer.recordType === 'string' ? offer.recordType.split('-')[0] : ''
  );

  /**
   * The latest in-cart offer Selleasy validated for this cart. Its script
   * appends to window.lbValidOffers on every validation (page load and each
   * cart request), spreading the offer config plus the trigger line.
   */
  function validatedOffer() {
    const list = window.lbValidOffers;
    if (!Array.isArray(list)) return null;
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const offer = list[i];
      if (offer && offer.widget === WIDGET_TYPE && shortIdOf(offer) && offer.isEnabled !== false) {
        return offer;
      }
    }
    return null;
  }

  function findOffer(shortId, widgetType) {
    const setup = window.lbSetup;
    if (!shortId || !setup || !Array.isArray(setup.offers)) return null;
    const now = Date.now();
    return setup.offers.find((offer) => {
      if (shortIdOf(offer) !== shortId) return false;
      if (offer.widget !== widgetType) return false;
      if (offer.isEnabled === false) return false;
      const schedule = offer.schedule;
      if (schedule) {
        if (schedule.startsAt && Number(schedule.startsAt) > now) return false;
        if (schedule.endsAt && Number(schedule.endsAt) < now) return false;
      }
      return true;
    }) || null;
  }

  /**
   * The offer's products in campaign order: [{ handle, productId, variantIds }].
   * Honours ignoreAlreadyBought (offer or block level) like Selleasy does.
   */
  function offerProducts(offer, root, limit) {
    const blocks = Array.isArray(offer.offerBlocks) ? offer.offerBlocks : [];
    const ignoreBoughtAll = !!(offer.configs && offer.configs.ignoreAlreadyBought);
    const inCart = new Set(
      Array.from(root.querySelectorAll('.cart-item[data-product-id]'))
        .map((line) => Number(line.dataset.productId))
        .filter((id) => Number.isFinite(id))
    );

    const products = [];
    const seen = new Set();
    blocks.forEach((block) => {
      if (!block || !Array.isArray(block.values)) return;
      const ignoreBought = ignoreBoughtAll || !!(block.configs && block.configs.ignoreAlreadyBought);
      block.values.forEach((value) => {
        if (!value || typeof value.handle !== 'string' || !value.handle) return;
        const productId = numericId(value.id);
        if (ignoreBought && productId && inCart.has(productId)) return;
        if (seen.has(value.handle)) return;
        seen.add(value.handle);
        products.push({
          handle: value.handle,
          productId,
          variantIds: (Array.isArray(value.variants) ? value.variants : [])
            .map((variant) => numericId(variant && variant.id))
            .filter((id) => id !== null),
          sortIndex: Number(value.sortIndex) || 0
        });
      });
    });

    products.sort((a, b) => a.sortIndex - b.sortIndex);
    return products.slice(0, limit);
  }

  /** The campaign's percentage discount, or 0 when it isn't a percentage. */
  function campaignPercent(offer) {
    const discount = offer && offer.discounts;
    if (!discount || discount.isDisabled || discount.type !== 'percentage') return 0;
    const value = Number(discount.value);
    return value > 0 && value < 100 ? value : 0;
  }

  /** The campaign's discount code, or '' when it has none. */
  function campaignCode(offer) {
    const discount = offer && offer.discounts;
    if (!discount || discount.isDisabled || typeof discount.code !== 'string') return '';
    return discount.code.trim();
  }

  /* ------------------------------------------------------------- discount */

  /**
   * Puts the campaign's discount code on the cart (keeping the codes already
   * there) unless it is there already. Shopify keeps a code that applies to
   * nothing yet as "not applicable" and applies it as soon as a line with
   * the _lb-product property is added. Resolves true when it had to be
   * applied. Checked at most once a minute unless forced.
   */
  async function ensureCode(code, force) {
    if (!code) return false;
    if (!force && state.codeCheckedAt && Date.now() - state.codeCheckedAt < CODE_TTL) return false;
    state.codeCheckedAt = Date.now();
    try {
      const cart = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null));
      if (!cart) return false;
      const codes = (cart.discount_codes || [])
        .map((entry) => entry && entry.code)
        .filter((entry) => typeof entry === 'string' && entry);
      if (codes.some((entry) => entry.toLowerCase() === code.toLowerCase())) return false;

      const response = await fetch(`${rootPath()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...codes, code].join(',') })
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  }

  /**
   * After an add from one of the campaign cards the theme re-renders the
   * drawer from the add response (product-form.js, ~150 ms later). If the
   * code had been dropped meanwhile (e.g. the customer applied another code,
   * which only keeps the applicable ones), put it back and re-render once
   * more so the line shows its discount.
   */
  function watchCampaignAdds() {
    document.addEventListener('on:cart:add', (evt) => {
      if (!state.code) return;
      const target = evt.target;
      if (!(target instanceof Element) || !target.closest('[data-upsell-track]')) return;
      setTimeout(async () => {
        const applied = await ensureCode(state.code, true);
        if (!applied) return;
        const drawer = getDrawer();
        if (drawer && typeof drawer.refresh === 'function') drawer.refresh(true);
      }, 400);
    }, true);
  }

  /* ---------------------------------------------------------------- price */

  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * Shows the campaign price on a card like Selleasy's widget did: the
   * discounted price, the regular one struck through and a "-25%" tag,
   * following the variant select (product-card-v3.js repaints the regular
   * price on change; this listener runs after it). The discount is floored
   * per line like the discount function does (25% of $5.99 → $4.50).
   */
  function applyCampaignPrice(li, percent) {
    if (!percent) return;
    const price = li.querySelector('.pcv3__price');
    const amount = li.querySelector('.pcv3__price-amount');
    const was = li.querySelector('.pcv3__price-was');
    if (!price || !amount) return;

    const select = li.querySelector('select[name="id"]');
    const single = li.querySelector('input[name="id"][data-price-cents]');
    const centsNow = () => {
      const source = select ? select.options[select.selectedIndex] : single;
      const cents = source && Number(source.dataset.priceCents);
      return Number.isFinite(cents) && cents > 0 ? cents : null;
    };

    const paint = () => {
      const cents = centsNow();
      if (cents === null) return;
      amount.textContent = formatMoney(cents - Math.floor((cents * percent) / 100));
      if (was) {
        was.textContent = formatMoney(cents);
        was.hidden = false;
      }
      let tag = price.querySelector(`.${PROMO_CLASS}`);
      if (!tag) {
        tag = document.createElement('span');
        tag.className = PROMO_CLASS;
        price.appendChild(tag);
      }
      tag.textContent = `-${percent}%`;
    };

    paint();
    if (select) select.addEventListener('change', paint);
  }

  /* ---------------------------------------------------------------- cards */

  function cachedCard(handle) {
    const entry = state.cards.get(handle);
    if (!entry) return null;
    if (Date.now() - entry.time > CARD_TTL) {
      state.cards.delete(handle);
      return null;
    }
    return entry;
  }

  async function fetchCards(el, handles) {
    const base = el.dataset.upsellCardsUrl;
    if (!base) throw new Error('cart-drawer-selleasy: no cards url');

    const url = `${base}${base.includes('?') ? '&' : '?'}q=${encodeURIComponent(handles.join(','))}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(response.status);

    const tmpl = document.createElement('template');
    tmpl.innerHTML = await response.text();
    const now = Date.now();
    tmpl.content.querySelectorAll('li[data-upsell-handle]').forEach((li) => {
      state.cards.set(li.dataset.upsellHandle, { html: li.outerHTML, time: now });
    });
    // Negative cache: missing, unpublished or sold-out products.
    handles.forEach((handle) => {
      if (!state.cards.has(handle)) state.cards.set(handle, { html: '', time: now });
    });
  }

  function cardElement(html) {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    return tmpl.content.querySelector('li[data-upsell-handle]');
  }

  /**
   * Keeps only the variants that are part of the offer. Returns false when
   * none of the card's variants is eligible (the card is dropped), true
   * otherwise; `restricted` tells whether the card's select was trimmed.
   */
  function restrictVariants(li, variantIds) {
    if (!variantIds.length) return { keep: true, restricted: false };

    const select = li.querySelector('select[name="id"]');
    if (!select) {
      const input = li.querySelector('input[name="id"]');
      if (!input) return { keep: true, restricted: false };
      return { keep: variantIds.includes(Number(input.value)), restricted: false };
    }

    const options = Array.from(select.options);
    const eligible = options.filter((option) => variantIds.includes(Number(option.value)));
    if (!eligible.length) return { keep: false, restricted: false };
    if (eligible.length === options.length) return { keep: true, restricted: false };

    options.forEach((option) => {
      if (!eligible.includes(option)) option.remove();
    });
    li.querySelectorAll('.custom-select__option[data-value]').forEach((option) => {
      if (!variantIds.includes(Number(option.dataset.value))) option.remove();
    });
    if (!eligible.some((option) => option.selected)) eligible[0].selected = true;
    return { keep: true, restricted: true };
  }

  function setLineProperty(li, value) {
    const form = li.querySelector('form.js-product-form');
    if (!form) return;
    let input = form.querySelector(`input[name="${PROPERTY_NAME}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = PROPERTY_NAME;
      form.appendChild(input);
    }
    input.value = value;
  }

  function showsOffer(el, shortId, triggerId) {
    if (el.dataset.upsellSource !== 'selleasy') return false;
    if (el.dataset.upsellOffer !== shortId || el.dataset.upsellTrigger !== triggerId) return false;
    const track = el.querySelector('[data-upsell-track]');
    return !!track && !!track.querySelector('li[data-upsell-handle]');
  }

  async function render(el, products, shortId, triggerId, offer, key) {
    const percent = campaignPercent(offer);
    const code = campaignCode(offer);
    const gen = ++state.gen;
    state.inflight = { el, key };

    const missing = products
      .map((product) => product.handle)
      .filter((handle) => !cachedCard(handle));

    if (missing.length) {
      el.classList.add('is-loading');
      try {
        await fetchCards(el, missing);
      } catch (e) {
        el.classList.remove('is-loading');
        if (gen === state.gen) markInactive(el);
        return;
      }
      el.classList.remove('is-loading');
    }

    if (gen !== state.gen) return;
    state.inflight = null;
    // The drawer re-rendered meanwhile: the observer syncs the new element.
    if (getUpsell() !== el) return;

    const track = el.querySelector('[data-upsell-track]');
    if (!track) {
      markInactive(el);
      return;
    }

    const items = [];
    const restrictedSelects = [];
    products.forEach((product) => {
      const entry = cachedCard(product.handle);
      if (!entry || !entry.html) return;
      const li = cardElement(entry.html);
      if (!li) return;
      const { keep, restricted } = restrictVariants(li, product.variantIds);
      if (!keep) return;
      setLineProperty(li, `${triggerId}#${shortId}`);
      items.push(li);
      if (restricted) restrictedSelects.push(li);
    });

    if (!items.length) {
      markInactive(el);
      return;
    }

    track.innerHTML = '';
    items.forEach((li) => track.appendChild(li));
    restrictedSelects.forEach((li) => {
      const select = li.querySelector('select[name="id"]');
      if (select) select.dispatchEvent(new Event('change', { bubbles: true }));
    });
    items.forEach((li) => applyCampaignPrice(li, percent));

    el.dataset.upsellSource = 'selleasy';
    el.dataset.upsellOffer = shortId;
    el.dataset.upsellTrigger = triggerId;

    const inner = el.querySelector('.cart-drawer-upsell__inner');
    if (inner) inner.hidden = false;
    const nav = el.querySelector('.cart-drawer-upsell__nav');
    if (nav) {
      nav.hidden = items.length <= 2;
      const prev = nav.querySelector('[data-upsell-nav="prev"]');
      const next = nav.querySelector('[data-upsell-nav="next"]');
      if (prev) prev.disabled = true;
      if (next) next.disabled = false;
    }
    track.scrollLeft = 0;
    holdDefault(el);
    reveal(el);

    state.status = 'active';
    clearWait();

    if (code !== state.code) state.codeCheckedAt = 0;
    state.code = code;
    ensureCode(code, false);
  }

  /* ----------------------------------------------------------------- sync */

  /**
   * Reconciles the carousel with Selleasy's widget. Idempotent: our own DOM
   * writes re-trigger the observer, which lands here and finds a consistent
   * state.
   */
  function sync() {
    const root = getDrawer();
    const el = getUpsell();
    if (!root || !el) return; // empty cart, or the drawer isn't on the page
    if (!isEnabledFor(el)) {
      reveal(el);
      return;
    }

    // Two signals that the campaign applies to this cart: the widget Selleasy
    // injects (freshest trigger id, but a drawer re-render can wipe it and
    // it only comes back after a cart request) and the validations it keeps
    // in window.lbValidOffers (never wiped). Either one is enough.
    const widget = root.querySelector(WIDGET_SELECTOR);
    const validated = validatedOffer();
    if (!widget && !validated) {
      // Nothing from Selleasy yet: give it a moment, then fall back. Before
      // it has ever answered, only a claim() (drawer open) starts that
      // clock, so a slow Selleasy load isn't mistaken for "no campaign".
      if (el.dataset.upsellSource === 'selleasy' || state.status === 'active') armWait();
      return;
    }

    const shortId = widget ? widget.getAttribute('offer') || '' : shortIdOf(validated);
    let offer = findOffer(shortId, widget ? widget.getAttribute('widget') : WIDGET_TYPE);
    if (!offer && validated && shortIdOf(validated) === shortId) offer = validated;

    const firstLine = root.querySelector('.cart-item[data-product-id]');
    const validatedTrigger = validated && validated.triggerItem && validated.triggerItem.lbId;
    const triggerId = (widget && widget.getAttribute('lb-trigger-product-id'))
      || (validatedTrigger ? String(validatedTrigger) : '')
      || (firstLine && firstLine.dataset.productId)
      || '';
    if (!offer || !triggerId) {
      markInactive(el);
      return;
    }

    const limit = Number(el.dataset.upsellLimit) || 8;
    const products = offerProducts(offer, root, limit);
    if (!products.length) {
      markInactive(el);
      return;
    }

    if (showsOffer(el, shortId, triggerId)) {
      holdDefault(el);
      state.status = 'active';
      clearWait();
      return;
    }

    const key = `${shortId}|${triggerId}|${campaignPercent(offer)}|${campaignCode(offer)}|${products.map((product) => product.handle).join(',')}`;
    if (state.inflight && state.inflight.el === el && state.inflight.key === key) return;

    render(el, products, shortId, triggerId, offer, key);
  }

  /**
   * Called by the theme before it loads the default recommendations into
   * `el`. Returns true when this module takes over (the caller must then
   * not call init()); false to keep the default flow.
   */
  function claim(el) {
    if (!isEnabledFor(el)) {
      releaseDefault(el);
      reveal(el);
      return false;
    }
    observe();
    if (state.status === 'inactive') {
      releaseDefault(el);
      reveal(el);
      return false;
    }
    holdDefault(el);
    armWait();
    scheduleSync();
    return true;
  }

  window.cibSelleasyUpsell = {
    claim,
    sync: scheduleSync,
    get status() {
      return state.status;
    }
  };

  // Observe from the start so a widget injected before the drawer is opened
  // (Selleasy runs on page load when the cart has items) already renders
  // the cards; the first claim() takes it from there.
  const start = () => {
    observe();
    watchCampaignAdds();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
